// /partner/switch — Switch to Fable+: import clients and services from another booking app's CSV export.
import React, { useEffect, useState } from "react";
import { ArrowRightLeft, Upload, Users, Scissors } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Card, Stat, inp } from "@/components/kit";

const SOURCES = { fresha: "Fresha", booksy: "Booksy", square: "Square", timely: "Timely", treatwell: "Treatwell", vagaro: "Vagaro", spreadsheet: "a spreadsheet" };
const err = (e) => (e && e.message) || "Something went wrong";

export default function PartnerSwitch() {
  const [kind, setKind] = useState("clients");
  const [csv, setCsv] = useState("");
  const [fileName, setFileName] = useState("");
  const [prev, setPrev] = useState(null);
  const [listings, setListings] = useState([]);
  const [listingId, setListingId] = useState("");
  const [basis, setBasis] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => { api("/provider/my-listings").then((d) => { const ls = Array.isArray(d) ? d : ((d && d.listings) || []); setListings(ls); if (ls[0]) setListingId(ls[0].id); }).catch(() => {}); }, []);
  const loadMine = () => api("/provider/switch/clients?" + new URLSearchParams({ q })).then((d) => setMine(d)).catch(() => setMine({ clients: [], count: 0 }));
  useEffect(() => { loadMine(); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (f) => {
    if (!f) return;
    if (f.size > 3000000) { toast.error("That file is too big — export up to 3 MB at a time."); return; }
    const rd = new FileReader();
    rd.onload = () => { setCsv(String(rd.result || "")); setFileName(f.name); setPrev(null); };
    rd.readAsText(f);
  };
  const preview = async () => {
    setBusy(true);
    try { const d = await api("/provider/switch/preview", { method: "POST", body: { kind, csv } }); setPrev(d); }
    catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  const commit = async () => {
    setBusy(true);
    try {
      const d = await api("/provider/switch/commit", { method: "POST", body: { kind, csv, listing_id: kind === "services" ? listingId : null, lawful_basis: basis } });
      toast.success("Imported " + d.added + " new " + kind + (d.updated ? ", updated " + d.updated : ""));
      setPrev(null); setCsv(""); setFileName(""); setBasis(false); loadMine();
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="partner-switch">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold"><ArrowRightLeft size={22} /> Switch to Fable+</h1>
        <p className="mt-1 text-sm text-muted-foreground">Bring your clients and services across from Fresha, Booksy, Square, Timely, Treatwell, Vagaro or any spreadsheet. Export a CSV from your old app — usually under Clients or Settings → Export — and upload it here.</p>
      </div>
      <div className="flex gap-2">
        {[["clients", "Clients", Users], ["services", "Services", Scissors]].map(([k, l, I]) => (
          <Button key={k} variant={kind === k ? "default" : "outline"} onClick={() => { setKind(k); setPrev(null); }} data-testid={"switch-kind-" + k}><I className="mr-1 h-4 w-4" />{l}</Button>
        ))}
      </div>
      <Card title={kind === "clients" ? "Upload your client list" : "Upload your service menu"} right={<Upload size={18} />}>
        <label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border px-4 py-8 text-center text-sm hover:border-primary">
          <input type="file" accept=".csv,text/csv,text/plain" className="hidden" onChange={(e) => pick(e.target.files && e.target.files[0])} data-testid="switch-file" />
          <Upload className="mb-2 h-6 w-6 text-muted-foreground" />
          {fileName ? <span className="font-semibold">{fileName}</span> : <span>Choose a CSV file</span>}
          <span className="mt-1 text-xs text-muted-foreground">Columns are matched automatically — you'll see a preview first.</span>
        </label>
        <Button className="mt-4" disabled={!csv || busy} onClick={preview} data-testid="switch-preview">{busy && !prev ? "Reading…" : "Preview"}</Button>
      </Card>
      {prev ? (
        <Card title={"Looks like an export from " + (SOURCES[prev.source] || "a spreadsheet")}>
          <div className="grid gap-3 sm:grid-cols-4">
            <Stat k={kind === "clients" ? "Clients found" : "Services found"} v={prev.total} />
            {kind === "clients" ? <><Stat k="With email" v={prev.with_email} /><Stat k="With mobile" v={prev.with_phone} /><Stat k="Opted in to marketing" v={prev.marketing_opt_in} /></>
              : <><Stat k="Missing a price" v={prev.missing_price} /><Stat k="Missing a duration" v={prev.missing_duration} /><Stat k="Rows skipped" v={prev.skipped} /></>}
          </div>
          <div className="mt-4 overflow-x-auto rounded-xl border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/50 text-xs text-muted-foreground"><tr>{kind === "clients" ? ["Name", "Email", "Mobile", "Notes"].map((h) => <th key={h} className="px-3 py-2">{h}</th>) : ["Service", "Price", "Minutes", "Category"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
              <tbody>{prev.sample.map((r, i) => (
                <tr key={i} className="border-t">{kind === "clients" ? <><td className="px-3 py-2">{r.name}</td><td className="px-3 py-2">{r.email}</td><td className="px-3 py-2">{r.phone}</td><td className="max-w-[240px] truncate px-3 py-2">{r.notes}</td></>
                  : <><td className="px-3 py-2">{r.name}</td><td className="px-3 py-2">{r.price == null ? "—" : "£" + Number(r.price).toFixed(2)}</td><td className="px-3 py-2">{r.duration || "—"}</td><td className="px-3 py-2">{r.category}</td></>}</tr>
              ))}</tbody>
            </table>
          </div>
          {kind === "services" ? (
            <label className="mt-4 block text-sm">Add these services to
              <select className={inp + " mt-1 max-w-md"} value={listingId} onChange={(e) => setListingId(e.target.value)} data-testid="switch-listing">
                {listings.map((l) => <option key={l.id} value={l.id}>{l.title}</option>)}
              </select>
            </label>
          ) : (
            <label className="mt-4 flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-1" checked={basis} onChange={(e) => setBasis(e.target.checked)} data-testid="switch-basis" />
              <span>These are my own clients and I'm allowed to move their details to Fable+. We keep them private to your business and never message them for you; only people the file marks as opted in count as opted in to marketing.</span>
            </label>
          )}
          <Button className="mt-4" disabled={busy || (kind === "clients" ? !basis : !listingId)} onClick={commit} data-testid="switch-commit">{busy ? "Importing…" : "Import " + prev.total + " " + kind}</Button>
        </Card>
      ) : null}
      <Card title={"Imported clients" + (mine ? " (" + mine.count + ")" : "")}>
        <input className={inp + " max-w-sm"} placeholder="Search by name" value={q} onChange={(e) => setQ(e.target.value)} />
        {mine && mine.clients.length ? (
          <div className="mt-3 max-h-[420px] overflow-auto rounded-xl border">
            <table className="w-full text-left text-sm"><tbody>{mine.clients.slice(0, 500).map((c) => (
              <tr key={c.id} className="border-t first:border-t-0"><td className="px-3 py-2 font-medium">{c.name}</td><td className="px-3 py-2 text-muted-foreground">{c.email}</td><td className="px-3 py-2 text-muted-foreground">{c.phone}</td><td className="px-3 py-2 text-xs text-muted-foreground">from {SOURCES[c.source] || c.source}</td></tr>
            ))}</tbody></table>
          </div>
        ) : <p className="mt-3 text-sm text-muted-foreground">No imported clients yet.</p>}
      </Card>
    </div>
  );
}
